import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Function to checks if the title component should be rendered
 */
const cardTitle = string => string ? <div className='font-medium whitespace-no-wrap text-sm xl:text-base'>{string}</div> : null

/**
 * Function to control the card subtitles (e.g. name and job)
 * 'string' is the string that will be output
 * 'color' checks if the component has a background color to make the text white
 * 'closingShift' and 'closingLabel' adds a red string if the shift has to close the restaurant
 */
const cardSubtitle = (string, color, closingShift, closingLabel) => {
  const closingElement = closingShift ? <span className='text-red-500 ml-2'>{closingLabel}</span> : null

  const classNames = [
    'text-xs',
    'xl:text-sm',
    'truncate',
    color === 'bg-white' ? 'text-gray-400' : 'text-white'
  ]

  return <div className={classNames.join(' ')}>{string || null}&nbsp;{closingElement}</div>
}

/**
 * Function to checks if the note component should be rendered
 */
const cardNote = (string, color) => {
  const classArray = [
    'w-4',
    'h-4',
    'ml-2',
    color === 'bg-white' ? 'text-primary-900' : 'text-white',
    color === 'bg-white' ? 'opacity-25' : 'opacity-50'
  ]

  const iconColor = color !== 'bg-white' ? 'text-white' : 'text-primary-900'
  const icon = (
    <svg className={classArray.join(' ')} width='16' height='16' viewBox='0 0 16 16'>
      <path fill='currentColor' d='M13.063 1h-10.125c-1.616 0-2.938 1.322-2.938 2.938v11.063l3-3h10.063c1.616 0 2.938-1.322 2.938-2.938v-5.125c0-1.616-1.322-2.938-2.938-2.938zM10 8h-6v-1h6v1zM12 6h-8v-1h8v1z' />
    </svg>
  )

  return string ? <div title={string}>{icon}</div> : null
}

/**
 * Function to add classes to the ShiftCard component
 * to control the background and text color
 */
const cardColor = color => {
  if (color === 'green') {
    return 'bg-available text-white'
  }

  if (color === 'orange') {
    return 'bg-pending text-white'
  }

  return color
}

/**
 * ShiftCard main component
 */
function ShiftCard ({ title, name, note, job, color, closingShift, closingLabel, children, onClick, active = false, shiftState = 'default' }) {
  const _className = classNames(
    'shift-card overflow-hidden rounded-lg cursor-pointer border-2 hover:border-gray-400 ',
    { 'border-gray-800': active },
    { 'border-gray-200': !active && shiftState === 'default' },
    { 'border-dashed': shiftState === 'mine-pending' || shiftState === 'pending' },
    { 'border-pending': !active && shiftState === 'mine-pending' },
    { 'border-available': !active && shiftState === 'pending' },
    { 'border-pending': !active && shiftState === 'awaiting_approval' },
    cardColor(color)
  )

  return (
    <div className={_className} onClick={onClick}>
      <div className='px-3 pt-3 pb-9 relative xl:px-4 xl:pt-5'>
        <div className='flex items-center flex-shrink-0'>
          {cardTitle(title)}
          {cardNote(note, color)}
        </div>
        {cardSubtitle(name, color)}
        {cardSubtitle(job, color, closingShift, closingLabel)}
        {children}
      </div>
    </div>
  )
}

ShiftCard.defaultProps = {
  color: 'bg-white',
  closingShift: false,
  closingLabel: 'Luk',
  onClick: null
}

ShiftCard.propTypes = {
  /**
     * Controls the text content of the box
     */
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]),
  /**
     * Add employee name to card (below title)
     */
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]),
  /**
     * Add (optional) note to card
     */
  note: PropTypes.string,
  /**
     * Add job name to card (below name)
     */
  job: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]),
  /**
     * Add a color to the card
     * Default colors: 'green', 'orange'
     */
  color: PropTypes.string,
  /**
     * Is this a shift that should close the restaurant?
     */
  closingShift: PropTypes.bool,
  /**
     * The text that will be added if this is a closing shift.
     * Defaults to "Luk"
     */
  closingLabel: PropTypes.string,
  /**
     * Add badges component to shift
     */
  children: PropTypes.node,
  /**
     * OnClick event handler
     */
  onClick: PropTypes.func,
  /**
     * Active status
     */
  active: PropTypes.bool
}

export default ShiftCard
